import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { Pressable, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { DragList } from '../dragList/dragList';
import { StyledTextInput } from './controlledTextInput';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type ControlledTextArrayInputProps = {
  label: string;
  addMoreLabel: string;
  name: string;
  control: Control;
  defaultValues?: string[];
  showResponseExceptions?: boolean;
  defaultExceptionValues?: string[];
  defaultMissingExceptionValues?: string[];
  exceptionsPrefix?: string;
  reorderable?: boolean;
};

export const ControlledTextArrayInput = (
  props: ControlledTextArrayInputProps
) => {
  const { append, remove, move } = useFieldArray({
    name: props.name,
    control: props.control,
  });

  const values = useWatch({
    name: props.name,
    control: props.control,
  });

  const exceptionsFieldName = props.exceptionsPrefix
    ? `${props.exceptionsPrefix}.responseExceptions`
    : 'responseExceptions';

  const missingExceptionsFieldName = props.exceptionsPrefix
    ? `${props.exceptionsPrefix}.responseMissingExceptions`
    : 'responseMissingExceptions';

  const exceptionValues = useWatch({
    name: props.showResponseExceptions ? exceptionsFieldName : '',
    control: props.control,
  });

  const missingExceptionValues = useWatch({
    name: props.showResponseExceptions ? missingExceptionsFieldName : '',
    control: props.control,
  });

  const { append: appendException, remove: removeException } = useFieldArray({
    name: props.showResponseExceptions ? exceptionsFieldName : '',
    control: props.control,
  });

  const { append: appendMissingException, remove: removeMissingException } =
    useFieldArray({
      name: props.showResponseExceptions ? missingExceptionsFieldName : '',
      control: props.control,
    });

  const [value, setValue] = useState('');
  const inputRef = useRef<any>(null);

  useEffect(() => {
    const defaultValues = props.defaultValues ?? [];
    const defaultExceptionValues = props.defaultExceptionValues ?? [];
    const defaultMissingExceptionValues =
      props.defaultMissingExceptionValues ?? [];

    defaultValues.forEach((value) => {
      if (!values || !values.includes(value)) {
        append(value);
      }
    });
    defaultExceptionValues.forEach((value) => {
      if (!exceptionValues || !exceptionValues.includes(value)) {
        appendException(value);
      }
    });
    defaultMissingExceptionValues.forEach((value: string) => {
      if (!missingExceptionValues || !missingExceptionValues.includes(value)) {
        appendMissingException(value);
      }
    });
  }, []);

  const onDelete = (item: string) => {
    remove(values.indexOf(item));
    if (props.showResponseExceptions && !exceptionValues?.includes(item)) {
      removeException(exceptionValues.indexOf(item));
    }
  };

  const isInvalidInput = () => {
    return !value || (values && values.some((v: string) => v === value));
  };

  const onAdd = () => {
    if (isInvalidInput()) {
      return;
    }
    append(value);
    setValue('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const onToggleException = (item: string) => {
    if (!props.showResponseExceptions) return;

    const isInRedExceptions = exceptionValues && exceptionValues.includes(item);
    const isInYellowExceptions =
      missingExceptionValues && missingExceptionValues.includes(item);

    if (isInRedExceptions) {
      // Currently red -> go to yellow
      removeException(exceptionValues.indexOf(item));
      appendMissingException(item);
    } else if (isInYellowExceptions) {
      // Currently yellow -> go to green
      removeMissingException(missingExceptionValues.indexOf(item));
    } else {
      // Currently green -> go to red
      appendException(item);
    }
  };

  const dragListItems = useMemo(
    () =>
      props.reorderable && values
        ? values.map((v: string, i: number) => ({
            id: `${i}-${v}`,
            value: v,
            order: i,
          }))
        : [],
    [props.reorderable, values]
  );

  const onReorder = (fromIndex: number, toIndex: number) => {
    move(fromIndex, toIndex);
  };

  const renderDragItem = useCallback(
    (item: { id: string; value: string; order: number }) => (
      <View style={styles.dragRowContent}>
        <ChoiceChip
          key={item.id}
          onClick={() => onToggleException(item.value)}
          onDelete={() => onDelete(item.value)}
          value={item.value}
          exceptionValues={exceptionValues}
          missingResponseExceptions={missingExceptionValues}
        />
      </View>
    ),
    [exceptionValues, missingExceptionValues]
  );

  return (
    <View>
      <View>
        <Text style={styles.label}>{props.label}</Text>
      </View>
      <StyledTextInput
        onChangeText={setValue}
        onBlur={() => {}}
        value={value}
        onSubmitEditing={onAdd}
        returnKeyType="done"
        innerRef={inputRef}
      />
      <View style={styles.optionsContainer}>
        {props.reorderable ? (
          <View style={styles.dragListBox}>
            {values && values.length > 0 && (
              <DragList
                list={dragListItems}
                pivotKey="id"
                sortKey="order"
                renderCustomItem={renderDragItem}
                onReorderedFromTo={onReorder}
                customItemStyle={styles.dragItem}
                activeItemStyle={styles.dragItemActive}
              />
            )}
          </View>
        ) : (
          <View style={styles.chipBox}>
            {values &&
              values.map((value: string) => (
                <ChoiceChip
                  key={value}
                  onClick={() => onToggleException(value)}
                  onDelete={() => onDelete(value)}
                  value={value}
                  exceptionValues={exceptionValues}
                  missingResponseExceptions={missingExceptionValues}
                />
              ))}
          </View>
        )}
        <Pressable>
          <Button
            disabled={isInvalidInput()}
            style={[styles.button]}
            buttonColor={Colors.primary}
            labelStyle={styles.labelStyle}
            mode="contained"
            onPress={onAdd}
          >
            {props.addMoreLabel}
          </Button>
        </Pressable>
      </View>
    </View>
  );
};

type ChoiceChipProps = {
  value: string;
  exceptionValues?: string[];
  missingResponseExceptions?: string[];
  onClick: () => void;
  onDelete: () => void;
};

const ChoiceChip = (props: ChoiceChipProps) => {
  const [chipStyle, setChipStyle] = useState(styles.chipGreen);

  useEffect(() => {
    if (props.exceptionValues?.includes(props.value)) {
      setChipStyle(styles.chipRed);
    } else if (props.missingResponseExceptions?.includes(props.value)) {
      setChipStyle(styles.chipYellow);
    } else {
      setChipStyle(styles.chipGreen);
    }
  }, [props.exceptionValues, props.value, props.missingResponseExceptions]);

  return (
    <View style={styles.chipContainer}>
      <Pressable onPress={props.onDelete} style={[styles.chipLeft, chipStyle]}>
        <MaterialCommunityIcons name="close" size={18} />
      </Pressable>
      <Pressable onPress={props.onClick} style={[styles.chipRight, chipStyle]}>
        <Text style={styles.chipText} selectable={false}>
          {props.value}
        </Text>
      </Pressable>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  optionsContainer: {
    width: 500,
    paddingTop: 20,
  },
  label: {
    fontSize: 16,
    lineHeight: 16,
    color: role.textStrong,
    marginBottom: 10,
    fontWeight: '700',
  },
  button: {
    borderRadius: 8,
    width: '100%',
    maxWidth: 156,
    height: 44,
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  labelStyle: {
    fontSize: 16,
    fontWeight: '500',
    color: role.textOnBrand,
  },
  chipBox: {
    paddingBottom: 20,
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flexDirection: 'row',
    rowGap: 8,
    columnGap: 4,
    maxHeight: 82,
    overflowY: 'auto',
    alignContent: 'flex-start',
  },
  dragListBox: {
    paddingBottom: 20,
    maxHeight: 200,
    overflowY: 'auto',
  },
  dragItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 0,
    borderBottomWidth: 0,
  },
  dragItemActive: {
    backgroundColor: role.surfaceRaised,
    borderRadius: 6,
  },
  dragRowContent: {
    flex: 1,
  },
  chipContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  chipText: {
    fontSize: 14,
    fontWeight: '500',
    display: 'flex',
    lineHeight: 18,
    marginLeft: 8,
    marginTop: 0,
    marginBottom: 0,
    cursor: 'pointer',
    columnGap: 4,
    marginRight: 8,
    color: role.textStrong,
  },
  chipLeft: {
    height: 20,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chipRight: {
    height: 20,
    paddingRight: 10,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    borderBottomLeftRadius: 0,
  },
  chipGreen: {
    backgroundColor: Colors.green,
  },
  chipRed: {
    backgroundColor: Colors.red,
  },
  chipYellow: {
    backgroundColor: Colors.yellow,
  },
}));
